import { TitreDocument } from '@/components/ui/titre-document';
import { SessionExpireeError, TraductionIntrouvableError } from '@/lib/api';
import { viderBrouillon } from '@/lib/brouillon';
import { lectureDuNoyau } from '@/lib/controle';
import { rechercheVersLogin } from '@/lib/garde-session';
import { useToast } from '@/lib/notifications';
import type { OngletAnalyse } from '@/lib/params-recherche';
import { effacerSession } from '@/lib/session';
import { MESSAGE_INTERROMPUE } from '@/lib/use-interruption';
import { useLectureAnalyse } from '@/lib/use-lecture-analyse';
import { useLectureControle } from '@/lib/use-lecture-controle';
import { useLectureTraduction } from '@/lib/use-lecture-traduction';
import type { NoyauCode } from '@rosetta/shared';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { AvantApres } from './avant-apres';
import { CadreAnalyse } from './cadre-analyse';
import { choisirNoyau } from './choisir-noyau';
import { EnteteAnalyse } from './entete-analyse';
import { OngletsAnalyse, libelleOngletAnalyse } from './onglets-analyse';
import { ServiceIndisponible } from './service-indisponible';
import { TraductionIntrouvable } from './traduction-introuvable';
import { VueTexteOrigine } from './vue-texte-origine';
import { VueTexteTraduit } from './vue-texte-traduit';

/** Puts the keyboard on the tab that bears `libelle` (the package gives its tabs no `id` to look for). */
function focaliserOnglet(libelle: string): void {
  for (const onglet of document.querySelectorAll<HTMLElement>('[role="tab"]')) {
    if (onglet.textContent === libelle) onglet.focus();
  }
}

interface EcranAnalyseProps {
  /** Identifier of the translation (the `$id` of the route). */
  id: string;
  /** The `?noyau=` of the URL, when there is a valid one. */
  noyau?: NoyauCode | undefined;
  onglet: OngletAnalyse;
  onChangerOnglet: (onglet: OngletAnalyse) => void;
}

/**
 * The analysis screen: it reads the translation, then shows one of its three tabs. "Avant /
 * après" puts the text of origin and the translated text of the noyau side by side; once the
 * translation is finished it also shows what Tahiti says about the text (#9): its purpose, above
 * the texts, and its invariants, marked in them and listed under them. Its additions (indicator
 * 2.3 of the quality control, #23) are marked in the translated
 * text, with a bubble. That reading is only an
 * addition: unreadable, absent or failed, the tab is what it was. "Texte d'origine" (#10) shows
 * the text with what the analysis says of it (who speaks and to whom, kind, tone, objective);
 * "Texte traduit" (#10) the translated text with what the quality control of Tahiti says of it.
 * In those two tabs, what Tahiti does not give is said to be "not available".
 *
 * The control is one sub-task per launch while the page shows every noyau of the text: it is read
 * for every launch of the document and merged (#34), so a noyau translated by another launch has
 * its control here. Without a `?noyau=` the page opens on a noyau a control covers. A control that
 * cannot be read ("unreadable": network, service) is told apart from one that does not exist, and
 * offers "réessayer"; a refused session on that read goes to `/login` like the translation's.
 *
 * What the reading can end in:
 *  - `echouee` or `bloquee` (#13): the address of the result, which shows the failure or the
 *    anomaly (no more toast and input screen, B4);
 *  - `partielle` (#13): the analysis of a noyau that has a translation (the one of the URL when
 *    it has one, the first that has one otherwise);
 *  - `interrompue` (#7): the toast that says so, then the history;
 *  - `en_cours`: read again every 5 s until a final status (a quiet wait);
 *  - `TraductionIntrouvableError`: "Cette traduction est introuvable." and "nouveau texte";
 *  - `SessionExpireeError`: the session is erased and the user goes to `/login`;
 *  - anything else (`ServiceIndisponibleError`): a sober message and "réessayer".
 *
 * Mount it with a `key` on the identifier: the redirections happen once per translation.
 */
export function EcranAnalyse({ id, noyau, onglet, onChangerOnglet }: EcranAnalyseProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const { data, error, refetch, isFetching } = useLectureTraduction(id, {
    // No automatic retry: "réessayer" is the user's call.
    retry: false,
  });
  const redirige = useRef(false);
  // A partial translation (#13) is analysed like a finished one, on a noyau that has a text.
  const lue = data?.statut === 'terminee' || data?.statut === 'partielle';
  const analyse = useLectureAnalyse(id, lue);
  const controle = useLectureControle(id, lue);

  // The control is read with the same session: a refusal there ends the session here too (#34).
  const sessionExpiree =
    error instanceof SessionExpireeError || controle.error instanceof SessionExpireeError;
  const echouee = error === null && (data?.statut === 'echouee' || data?.statut === 'bloquee');
  const interrompue = error === null && data?.statut === 'interrompue';

  // Not a fetch: the reading is the query above. These are the two exits of the
  // screen, which must leave the page rather than draw something.
  useEffect(() => {
    if (redirige.current) return;
    if (sessionExpiree) {
      redirige.current = true;
      effacerSession();
      void navigate({ to: '/login', search: rechercheVersLogin('session-expiree') });
    } else if (echouee) {
      redirige.current = true;
      void navigate({ to: '/resultat/$id', params: { id }, replace: true });
    } else if (interrompue) {
      redirige.current = true;
      toast.afficher({ variant: 'neutre', message: MESSAGE_INTERROMPUE });
      void navigate({ to: '/historique', replace: true });
    }
  }, [sessionExpiree, echouee, interrompue, navigate, toast, id]);

  if (error instanceof TraductionIntrouvableError) {
    return (
      <TraductionIntrouvable
        onNouveauTexte={() => {
          viderBrouillon();
          void navigate({ to: '/' });
        }}
      />
    );
  }

  // A noyau another launch of the document is still translating (#36, until that launch can be read,
  // its quality control included) is not analysed yet: "Voir aussi" lists it with its ring, and the
  // page neither opens on it nor takes it from the address.
  const enCoursAilleurs = new Set((data?.noyauxEnCours ?? []).map((ligne) => ligne.noyau));
  const traduits = data?.resultats
    .filter((resultat) => resultat.texte !== undefined && !enCoursAilleurs.has(resultat.noyau))
    .map((resultat) => resultat.noyau);
  const candidats =
    data === undefined
      ? []
      : data.statut === 'partielle'
        ? (traduits ?? [])
        : data.noyaux.filter((code) => !enCoursAilleurs.has(code));
  // Without a noyau in the URL the page opens on one a control covers (#34): the first choice
  // waits for that read, rather than opening on a noyau and jumping to another.
  const choixEnAttente =
    lue && controle.isPending && candidats.length > 1 && !candidats.some((code) => code === noyau);
  const noyauAffiche =
    data === undefined
      ? undefined
      : choisirNoyau(
          candidats,
          noyau,
          controle.data?.noyaux.map((entree) => entree.noyau),
        );
  // A translation with no noyau is not a valid one: treated as an unexpected answer.
  const inattendu = lue && noyauAffiche === undefined;

  if ((error !== null && !sessionExpiree) || inattendu) {
    return (
      <ServiceIndisponible
        enCours={isFetching}
        onReessayer={() => {
          void refetch();
        }}
      />
    );
  }

  if (data === undefined || !lue || noyauAffiche === undefined || choixEnAttente) {
    return (
      <CadreAnalyse titre="Analyse des modifications">
        <EnteteAnalyse id={id} />
        <output className="mt-12 block font-light text-gray-700 text-body-lg">
          Chargement de l'analyse...
        </output>
      </CadreAnalyse>
    );
  }

  const lectureControle = lectureDuNoyau(controle, noyauAffiche);
  const controleAffiche = typeof lectureControle === 'string' ? undefined : lectureControle;
  const apres = data.resultats.find((resultat) => resultat.noyau === noyauAffiche)?.texte ?? '';

  return (
    <CadreAnalyse titre="Analyse des modifications">
      <EnteteAnalyse
        id={id}
        noyau={noyauAffiche}
        traduits={candidats}
        onglet={onglet}
        enCours={data.noyauxEnCours ?? []}
      />
      <TitreDocument titre={data.titre} />
      <OngletsAnalyse
        actif={onglet}
        onChange={onChangerOnglet}
        contenu={
          onglet === 'avant-apres' ? (
            <AvantApres
              avant={data.texteOrigine}
              apres={apres}
              analyse={analyse.data}
              controle={controleAffiche}
            />
          ) : onglet === 'texte-origine' ? (
            <VueTexteOrigine
              texte={data.texteOrigine}
              profil={analyse.isPending ? 'chargement' : (analyse.data?.profil ?? 'indisponible')}
            />
          ) : (
            <VueTexteTraduit
              texte={apres}
              controle={lectureControle}
              onReessayer={() => {
                void controle.refetch();
              }}
              reessaiEnCours={controle.isFetching}
              onVoirAvantApres={() => {
                onChangerOnglet('avant-apres');
                // The link is gone with the tab: the keyboard goes on from the tab it led to.
                focaliserOnglet(libelleOngletAnalyse('avant-apres'));
              }}
            />
          )
        }
      />
    </CadreAnalyse>
  );
}
